import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Services', href: '#services' },
    { label: 'Capabilities', href: '#capabilities' },
    { label: 'Industries', href: '#industries' },
    { label: 'Work', href: '#work' },
    { label: 'About', href: '#about' },
    { label: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-[#06121E]/85 backdrop-blur-xl border-b border-[rgba(120,170,210,0.18)] shadow-[0_1px_0_0_rgba(25,181,255,0.08)]'
            : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className={`flex items-center justify-between transition-all duration-500 ${isScrolled ? 'h-16' : 'h-20'}`}>
            {/* Logo */}
            <a href="#hero" onClick={(e) => scrollToSection(e, '#hero')} className="flex items-center gap-3 group">
              <img
                src="https://customer-assets.emergentagent.com/job_09644332-5e2e-4361-bc30-b66a2a3d7f1d/artifacts/qxe7l1b6_Jixdo_Monogram.png"
                alt="Jixdo monogram"
                className={`transition-all duration-500 ${isScrolled ? 'h-8 w-8' : 'h-10 w-10'} group-hover:scale-105`}
              />
              <div className="flex flex-col leading-none">
                <span className="text-xl font-extrabold text-[#EAF4FF] tracking-tight">Jixdo</span>
                <span className="text-[9px] uppercase tracking-[0.25em] text-[#A9BDD1] font-medium mt-0.5 hidden sm:block">
                  Operating Partner
                </span>
              </div>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="px-4 py-2 text-[13px] font-medium text-[#A9BDD1] hover:text-[#EAF4FF] transition-colors duration-300 relative group"
                >
                  {link.label}
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-px bg-[#19B5FF] group-hover:w-6 transition-all duration-300"></span>
                </a>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <span className="w-px h-6 bg-[rgba(120,170,210,0.18)]"></span>
              <Button
                onClick={(e) => scrollToSection(e, '#contact')}
                className="bg-[#19B5FF] hover:bg-[#48C8FF] text-[#06121E] font-semibold px-5 py-2 h-auto rounded-md transition-all duration-300 hover:shadow-lg hover:shadow-[#19B5FF]/30 text-sm"
              >
                Schedule Consultation
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-[#EAF4FF] hover:text-[#19B5FF] transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-[#06121E]/98 backdrop-blur-lg" onClick={() => setIsMobileMenuOpen(false)}></div>
        <div className="relative h-full flex flex-col justify-center items-center space-y-6 pt-20">
          {navLinks.map((link, index) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => scrollToSection(e, link.href)}
              className="text-2xl font-semibold text-[#EAF4FF] hover:text-[#19B5FF] transition-colors duration-300"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {link.label}
            </a>
          ))}
          <Button
            onClick={(e) => scrollToSection(e, '#contact')}
            className="bg-[#19B5FF] hover:bg-[#48C8FF] text-[#06121E] font-semibold px-8 py-3 rounded-lg mt-8 transition-all duration-300"
          >
            Schedule Consultation
          </Button>
        </div>
      </div>
    </>
  );
};